import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useNavigate } from 'react-router-dom';
import { SEC2_COLUMNS, ExpenseItem, getExpenseTotalAmount, downloadIndividualExpensesSpreadsheet } from './expenseUtils';

interface IndividualExpensesProps {
  submittedExpenses: ExpenseItem[];
  downloadingSpreadsheet: boolean;
  onDownloadSpreadsheet: () => void;
}

export default function IndividualExpenses({
  submittedExpenses,
  downloadingSpreadsheet,
  onDownloadSpreadsheet,
}: IndividualExpensesProps) {
  const navigate = useNavigate();

  // State
  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [colMenuAnchor, setColMenuAnchor] = useState<HTMLElement | null>(null);
  const [visibleCols, setVisibleCols] = useState<Set<string>>(
    () => new Set(SEC2_COLUMNS.filter(c => c.key !== 'link' && c.key !== 'items').map(c => c.key))
  );

  // Helper functions
  const col = (k: string) => visibleCols.has(k);

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(p => p === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const rowBg = (level: string) => {
    if (level === 'withinApprovalLevel') return '#e8f5e9';
    if (level === 'aboveApprovalLevelButWithinThreshold') return '#fff3e0';
    if (level === 'aboveApprovalLevel') return '#ffebee';
    return undefined;
  };

  // Color palettes for category cycling (same as By Category tab)
  const categoryPalettes = [
    { bg: '#e3f2fd', border: '#1976d2' }, // Blue
    { bg: '#e8f5e9', border: '#2e7d32' }, // Green
    { bg: '#f3e5f5', border: '#7b1fa2' }, // Purple
    { bg: '#fff3e0', border: '#e65100' }, // Orange
    { bg: '#e0f7fa', border: '#00838f' }, // Teal
  ];

  // Data processing
  const rows = submittedExpenses.map((expense, idx) => {
    const ai = (expense as any).ApprovedItem || (expense as any).approvedItem || {};
    const ie = ai.item_estimate || {};
    const ar = ai.aproval_request || {};
    const approvalRequestId: string | undefined = ar.documentId || ar.id;
    const category: string = ar.title || ie.item || (expense.isExceptionRequest ? 'Exception' : '-');
    const spent = getExpenseTotalAmount(expense);
    const approvedBudget =
      expense.isExceptionRequest && (expense as any).ExceptionApproval?.status === 'Approved'
        ? spent
        : expense.isExceptionRequest && (expense as any).ExceptionApproval?.status === 'Rejected'
        ? 0
        : ai.totalPrice || 0;
    const remaining: number = ai.remianingAmount ?? ai.remainingAmount ?? 0;
    const level: string = (expense as any).budgetWithinApprovalLevel || '';
    const exStatus: string = (expense as any).ExceptionApproval?.status || (expense.isExceptionRequest ? 'Pending' : '');
    const docId: string = (expense as any).documentId || String(expense.id);
    return {
      slno: idx + 1, docId,
      title: expense.title || '',
      date: expense.createdAt || '',
      category, approvalRequestId,
      approvedBudget, spent, remaining,
      itemCount: expense.expenseItems?.length || 0,
      itemsList: (expense.expenseItems || []).map((it: any) => it.item || '').filter(Boolean).join(', '),
      exception: expense.isExceptionRequest,
      exStatus, level,
    };
  });

  // Filter
  const filtered = rows.filter(r => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      r.title.toLowerCase().includes(q) ||
      r.category.toLowerCase().includes(q) ||
      r.exStatus.toLowerCase().includes(q)
    );
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    const d = sortDir === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'title':         return d * a.title.localeCompare(b.title);
      case 'date':          return d * (new Date(a.date).getTime() - new Date(b.date).getTime());
      case 'category':      return d * a.category.localeCompare(b.category);
      case 'approvedBudget': return d * (a.approvedBudget - b.approvedBudget);
      case 'spent':         return d * (a.spent - b.spent);
      case 'remaining':     return d * (a.remaining - b.remaining);
      case 'itemCount':     return d * (a.itemCount - b.itemCount);
      default: return 0;
    }
  });

  if (submittedExpenses.length === 0) {
    return <Typography color="textSecondary">No expenses submitted yet.</Typography>;
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, backgroundColor: '#fafafa', borderBottom: '1px solid #ebebeb', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Filter by title, category or status…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          sx={{ minWidth: 260, '& .MuiInputBase-input': { fontSize: '0.82rem', py: '6px' } }}
        />
        <Typography sx={{ fontSize: '0.75rem', color: '#aaa' }}>
          {sorted.length} / {rows.length} row{rows.length !== 1 ? 's' : ''}
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={e => setColMenuAnchor(e.currentTarget)}
            sx={{ ml: 1, textTransform: 'none', fontSize: '0.78rem', borderColor: '#d0d0d0', color: '#555', minWidth: 0, px: 1.5 }}
          >
            ⊞ Columns
          </Button>
          <Menu
            anchorEl={colMenuAnchor}
            open={Boolean(colMenuAnchor)}
            onClose={() => setColMenuAnchor(null)}
            PaperProps={{ sx: { minWidth: 190, px: 0.5, py: 0.5 } }}
          >
            {SEC2_COLUMNS.map(c => (
              <MenuItem key={c.key} dense disableRipple sx={{ py: 0.25, px: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={visibleCols.has(c.key)}
                      onChange={() => setVisibleCols(prev => {
                        const next = new Set(prev);
                        next.has(c.key) ? next.delete(c.key) : next.add(c.key);
                        return next;
                      })}
                      sx={{ p: 0.5 }}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.8rem' }}>{c.label}</Typography>}
                  sx={{ m: 0 }}
                />
              </MenuItem>
            ))}
          </Menu>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => downloadIndividualExpensesSpreadsheet(submittedExpenses)}
            disabled={downloadingSpreadsheet || submittedExpenses.length === 0}
            sx={{ textTransform: 'none', fontSize: '0.8rem', borderColor: '#388e3c', color: '#388e3c', '&:hover': { backgroundColor: '#f1f8f1', borderColor: '#2e7d32' } }}
          >
            {downloadingSpreadsheet ? 'Downloading…' : 'Download Spreadsheet'}
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer sx={{ maxHeight: 560 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {col('slno') && (
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', width: 44, backgroundColor: '#f5f5f5' }}>#</TableCell>
              )}
              {col('title') && (
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 160, backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel active={sortField === 'title'} direction={sortField === 'title' ? sortDir : 'asc'} onClick={() => handleSort('title')}>Title</TableSortLabel>
                </TableCell>
              )}
              {col('date') && (
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 105, backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel active={sortField === 'date'} direction={sortField === 'date' ? sortDir : 'asc'} onClick={() => handleSort('date')}>Date</TableSortLabel>
                </TableCell>
              )}
              {col('approvedBudget') && (
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 110, backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel active={sortField === 'approvedBudget'} direction={sortField === 'approvedBudget' ? sortDir : 'asc'} onClick={() => handleSort('approvedBudget')}>Approved</TableSortLabel>
                </TableCell>
              )}
              {col('spent') && (
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 90, backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel active={sortField === 'spent'} direction={sortField === 'spent' ? sortDir : 'asc'} onClick={() => handleSort('spent')}>Spent</TableSortLabel>
                </TableCell>
              )}
              {col('remaining') && (
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 105, backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel active={sortField === 'remaining'} direction={sortField === 'remaining' ? sortDir : 'asc'} onClick={() => handleSort('remaining')}>Remaining</TableSortLabel>
                </TableCell>
              )}
              {col('exception') && (
                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem', width: 85, backgroundColor: '#f5f5f5' }}>Exception</TableCell>
              )}
              {col('itemsList') && (
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 200, backgroundColor: '#f5f5f5' }}>Items List</TableCell>
              )}
              {col('items') && (
                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem', width: 65, backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel active={sortField === 'itemCount'} direction={sortField === 'itemCount' ? sortDir : 'asc'} onClick={() => handleSort('itemCount')}>Item Count</TableSortLabel>
                </TableCell>
              )}
              {col('category') && (
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 140, backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel active={sortField === 'category'} direction={sortField === 'category' ? sortDir : 'asc'} onClick={() => handleSort('category')}>Category</TableSortLabel>
                </TableCell>
              )}
              {col('status') && (
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 120, backgroundColor: '#f5f5f5' }}>Approval Status</TableCell>
              )}
              {col('link') && (
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 100, backgroundColor: '#f5f5f5' }}>Detail Link</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((r, ri) => {
                const palette = categoryPalettes[ri % categoryPalettes.length];
                return (
              <TableRow
                key={r.docId}
                onClick={() => navigate(`/expense/${r.docId}`)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: palette.bg,
                  borderLeft: `3px solid ${palette.border}`,
                  '&:last-child td': { borderBottom: 0 },
                }}
              >
                {col('slno') && (
                  <TableCell sx={{ fontSize: '0.75rem', color: '#bbb' }}>{ri + 1}</TableCell>
                )}
                {col('title') && (
                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.title || '(Untitled)'}
                  </TableCell>
                )}
                {col('date') && (
                  <TableCell sx={{ fontSize: '0.75rem', color: '#555', whiteSpace: 'nowrap' }}>
                    {r.date ? new Date(r.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </TableCell>
                )}
                {col('approvedBudget') && (
                  <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                    ₹{r.approvedBudget.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                )}
                {col('spent') && (
                  <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1976d2' }}>
                    ₹{r.spent.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                )}
                {col('remaining') && (
                  <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, color: r.remaining < 0 ? '#d32f2f' : '#388e3c' }}>
                    ₹{r.remaining.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                )}
                {col('exception') && (
                  <TableCell align="center">
                    {r.exception
                      ? <Chip label="Yes" size="small" color="warning" />
                      : <Chip label="No" size="small" variant="outlined" />}
                  </TableCell>
                )}
                {col('itemsList') && (
                  <TableCell sx={{ fontSize: '0.75rem', color: '#555', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.itemsList || <Typography sx={{ fontSize: '0.72rem', color: '#ccc' }}>—</Typography>}
                  </TableCell>
                )}
                {col('items') && (
                  <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{r.itemCount}</TableCell>
                )}
                {col('category') && (
                  <TableCell sx={{ fontSize: '0.75rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.approvalRequestId ? (
                      <a
                        href={`/approval/${r.approvalRequestId}`}
                        onClick={e => e.stopPropagation()}
                        style={{ color: '#1976d2', textDecoration: 'none', fontSize: '0.75rem' }}
                        onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                      >
                        {r.category}
                      </a>
                    ) : r.category}
                  </TableCell>
                )}
                {col('status') && (
                  <TableCell>
                    {r.exStatus
                      ? <Chip label={r.exStatus} size="small" color={r.exStatus === 'Approved' ? 'success' : r.exStatus === 'Rejected' ? 'error' : 'default'} />
                      : <Typography sx={{ fontSize: '0.72rem', color: '#ccc' }}>—</Typography>}
                  </TableCell>
                )}
                {col('link') && (
                  <TableCell onClick={e => e.stopPropagation()}>
                    <a
                      href={`/expense/${r.docId}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: '0.75rem', color: '#1976d2', textDecoration: 'none' }}
                      onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                    >
                      View
                    </a>
                  </TableCell>
                )}
              </TableRow>
                );
              })}
          </TableBody>
          {/* Totals Footer */}
          <TableHead>
            <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
              {col('slno') && <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }} />}
              {col('title') && <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Total</TableCell>}
              {col('date') && <TableCell />}
              {col('approvedBudget') && (
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                  ₹{sorted.reduce((sum, r) => sum + r.approvedBudget, 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </TableCell>
              )}
              {col('spent') && (
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1976d2' }}>
                  ₹{sorted.reduce((sum, r) => sum + r.spent, 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </TableCell>
              )}
              {col('remaining') && (() => {
                const totalRemaining = sorted.reduce((sum, r) => sum + r.remaining, 0);
                return (
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', color: totalRemaining < 0 ? '#d32f2f' : '#388e3c' }}>
                    ₹{totalRemaining.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </TableCell>
                );
              })()}
              {col('exception') && <TableCell />}
              {col('itemsList') && <TableCell />}
              {col('items') && <TableCell />}
              {col('category') && <TableCell />}
              {col('status') && <TableCell />}
              {col('link') && <TableCell />}
            </TableRow>
          </TableHead>
        </Table>
      </TableContainer>
    </Paper>
  );
}
